import React, {
  FC,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './RentalBasicSettings.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Input from '../../../../components/Input';
import SelectComponent from '../../../../components/SelectComponent';
import {
  ButtonType,
  FieldTypes,
  FormattedImageListType,
  InputType,
  TabsNames,
} from '../../../../utils/@globalTypes';
import Button from '../../../../components/Button';
import { RoutesList } from '../../../../App';
import {
  RentalObjectSelectors,
  updateRentalObject,
  updateRentalObjectAllBasicSettings,
  updateRentalObjectImages,
} from '../../../../redux/reducers/rentalObjectSlice';
import {
  OBJECT_NAME_REQ,
  RENTAL_DESCRIPTION_REQ,
  RENTAL_OBJECT_IMAGES_REQ,
  SHORT_DESCRIPTION_REQ,
} from '../../../../utils/constants';
import { banDoubleSpace, setFieldErrorText } from '../../../../utils/functions';
import ImageMultipleComponent from '../../../../components/ImageMultipleComponent';
import { UpdateRentalObjectData } from '../../../../redux/types/rentalObjectTypes';
import TextEditor from '../../../../components/TextEditor';
import FeaturesBlock from '../../../../components/FeaturesBlock';

const RentalBasicSettings: FC = () => {
  const dispatch = useDispatch();
  const { mainId, rentalId } = useParams();

  const rentalObjectTypes = useSelector(
    RentalObjectSelectors.getRentalObjectTypes,
  );
  const rentalObjectStatuses = useSelector(
    RentalObjectSelectors.getRentalObjectStatuses,
  );
  const currentRentalObject = useSelector(
    RentalObjectSelectors.getCurrentRentalObject,
  );

  const currentRentalImages = useSelector(
    RentalObjectSelectors.getCurrentImagesList,
  );
  const saveButtonRef = useRef<HTMLDivElement | null>(null);
  const [rentalObjName, setRentalObjName] = useState('');
  const [rentalObjType, setRentalObjType] = useState('');
  const [rentalObjStatus, setRentalObjStatus] = useState('');
  const [maxPlaces, setMaxPlaces] = useState('');
  const [rentalObjDescription, setRentalObjDescription] = useState('');
  const [shortObjDescription, setShortObjDescription] = useState('');

  // const [images, setImages] = useState<FormattedImageListType>([]);
  const [newImages, setNewImages] = useState<FormattedImageListType>([]);
  const [removedImages, setRemovedImages] = useState<FormattedImageListType>(
    [],
  );
  const [newImagesError, setNewImagesError] = useState('');

  const [rentalObjNameError, setRentalObjNameError] = useState('');
  const [rentalObjTypeError, setRentalObjTypeError] = useState('');
  const [rentalObjDescriptionError, setRentalObjDescriptionError] =
    useState('');
  const [shortObjDescriptionError, setShortObjDescriptionError] = useState('');
  const [maxPlacesError, setMaxPlacesError] = useState('');

  const [rentalObjNameTouched, setRentalObjNameTouched] = useState(false);
  const [rentalObjTypeTouched, setRentalObjTypeTouched] = useState(false);
  const [rentalObjDescriptionTouched, setRentalObjDescriptionTouched] =
    useState(false);
  const [shortObjDescriptionTouched, setShortObjDescriptionTouched] =
    useState(false);
  const [maxPlacesTouched, setMaxPlacesTouched] = useState(false);

  const onSaveObjectBtnClick = () => {
    setRentalObjNameTouched(true);
    setRentalObjTypeTouched(true);
    setRentalObjDescriptionTouched(true);
    setShortObjDescriptionTouched(true);
    setMaxPlacesTouched(true);

    if (
      rentalId &&
      !!currentRentalObject &&
      !rentalObjNameError &&
      !rentalObjDescriptionError &&
      !shortObjDescriptionError &&
      !rentalObjTypeError &&
      !maxPlacesError &&
      !newImagesError &&
      !newImages.some((image) => image.value.error) &&
      rentalObjName.length > 0 &&
      rentalObjDescription.length > 0 &&
      shortObjDescription.length > 0 &&
      rentalObjType.length > 0 &&
      maxPlaces.length > 0 &&
      Number(maxPlaces) > 0
    ) {
      const data: UpdateRentalObjectData = {};
      let images: FormattedImageListType = [];

      const {
        name,
        description,
        status,
        object_type,
        max_places,
        short_description,
      } = currentRentalObject;

      name !== rentalObjName && (data.name = rentalObjName);
      description !== rentalObjDescription &&
        (data.description = rentalObjDescription);
      short_description !== shortObjDescription &&
        (data.short_description = shortObjDescription);
      max_places !== +maxPlaces && (data.max_places = +maxPlaces);
      status !== rentalObjStatus && (data.status = rentalObjStatus);
      object_type.id !== +rentalObjType && (data.object_type = +rentalObjType);
      JSON.stringify(currentRentalImages.map((item) => item.image)) !==
        JSON.stringify(newImages.map((item) => item.value.image)) &&
        (images = newImages);

      dispatch(
        updateRentalObjectAllBasicSettings({
          info: Object.keys(data).length
            ? {
                id: +rentalId,
                data,
              }
            : undefined,
          images:
            removedImages.length || images.length
              ? {
                  rental_object: +rentalId,
                  images,
                  removedIdList: removedImages.map((image) => image.id),
                  clearRemovedListCallback: () => setRemovedImages([]),
                }
              : undefined,
        }),
      );

      setNewImages([]);
    }
  };

  useLayoutEffect(() => {
    if (currentRentalObject) {
      const {
        name,
        object_type,
        status,
        max_places,
        description,
        short_description,
      } = currentRentalObject;

      setRentalObjName(name);
      description && setRentalObjDescription(description);
      short_description && setShortObjDescription(short_description);
      setRentalObjStatus(status);
      setRentalObjType(object_type.id.toString());
      max_places && setMaxPlaces(max_places.toString());
      setNewImages(
        currentRentalImages.map((item) => ({
          id: item.id,
          value: {
            image: item.image,
            error: '',
            isNew: false,
          },
        })),
      );
    }
  }, [currentRentalObject, currentRentalImages]);

  // Validation

  // Object name

  useEffect(() => {
    setFieldErrorText(
      rentalObjNameTouched,
      rentalObjName,
      setRentalObjNameError,
      FieldTypes.OBJECT_NAME,
      true,
    );
  }, [rentalObjNameTouched, rentalObjName]);

  // Object type

  useEffect(() => {
    setFieldErrorText(
      rentalObjTypeTouched,
      rentalObjType,
      setRentalObjTypeError,
      FieldTypes.DEFAULT,
      true,
    );
  }, [rentalObjTypeTouched, rentalObjType]);

  // Max places

  useEffect(() => {
    setFieldErrorText(
      maxPlacesTouched,
      maxPlaces,
      setMaxPlacesError,
      FieldTypes.MAX_PLACES,
      true,
    );
  }, [maxPlacesTouched, maxPlaces]);

  // Description

  useEffect(() => {
    setFieldErrorText(
      rentalObjDescriptionTouched,
      rentalObjDescription,
      setRentalObjDescriptionError,
      FieldTypes.RENTAL_DESCRIPTION,
      true,
    );
  }, [rentalObjDescriptionTouched, rentalObjDescription]);

  useEffect(() => {
    setFieldErrorText(
      shortObjDescriptionTouched,
      shortObjDescription,
      setShortObjDescriptionError,
      FieldTypes.SHORT_DESCRIPTION,
      true,
    );
  }, [shortObjDescriptionTouched, shortObjDescription]);

  const isValid = useMemo(() => {
    return (
      !rentalObjNameError &&
      !rentalObjDescriptionError &&
      !shortObjDescriptionError &&
      !rentalObjTypeError &&
      !maxPlacesError &&
      !newImages.some((image) => image.value.error)
    );
  }, [
    rentalObjNameError,
    rentalObjDescriptionError,
    shortObjDescriptionError,
    rentalObjTypeError,
    maxPlacesError,
    newImages,
  ]);
  const [isSaveVisible, setIsSaveVisible] = useState(true);
  useEffect(() => {
    if (!saveButtonRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSaveVisible(entry.isIntersecting);
      },
      {
        root: null, // viewport
        threshold: 0.1,
      },
    );

    observer.observe(saveButtonRef.current);

    return () => observer.disconnect();
  }, []);

  const isFieldsChanged = useMemo(() => {
    if (!!currentRentalObject) {
      const {
        name,
        description,
        status,
        object_type,
        max_places,
        short_description,
      } = currentRentalObject;

      return (
        name !== rentalObjName ||
        description !== rentalObjDescription ||
        short_description !== shortObjDescription ||
        status !== rentalObjStatus ||
        object_type.id !== +rentalObjType ||
        max_places !== +maxPlaces ||
        JSON.stringify(currentRentalImages.map((item) => item.image)) !==
          JSON.stringify(newImages.map((item) => item.value.image))
      );
    }
  }, [
    rentalObjName,
    rentalObjDescription,
    shortObjDescription,
    rentalObjStatus,
    rentalObjType,
    maxPlaces,
    currentRentalObject,
    newImages,
    currentRentalImages,
  ]);

  const showFloatingSave = isFieldsChanged && !isSaveVisible;

  return (
    <>
      <div className={styles.settingsWrapper}>
        <div>
          <h3 className={styles.contentTitle}>Объект</h3>
          <div className={styles.objWrapper}>
            <div className={styles.objTopWrapper}>
              <Input
                value={rentalObjName}
                title="Наименование сдаваемого объекта*"
                placeholder="Введите наименование объекта"
                type={InputType.TEXT}
                onChange={setRentalObjName}
                errText={rentalObjNameError}
                onBlur={setRentalObjNameTouched}
                requirementsText={OBJECT_NAME_REQ}
                customBanSymbols={banDoubleSpace}
              />
              <SelectComponent
                title="Вид сдаваемого объекта*"
                optionsList={rentalObjectTypes}
                currentValue={rentalObjType}
                setSelecValue={setRentalObjType}
                errText={rentalObjTypeError}
                isLoading={rentalObjectTypes.length === 0}
                onBlur={setRentalObjTypeTouched}
              />
              <SelectComponent
                title="Статус объекта*"
                optionsList={rentalObjectStatuses}
                currentValue={rentalObjStatus}
                setSelecValue={setRentalObjStatus}
                isLoading={rentalObjectStatuses.length === 0}
              />
              <div>
                <Input
                  value={maxPlaces}
                  title="Максимальное количество мест*"
                  placeholder="0"
                  type={InputType.MASK}
                  onChange={setMaxPlaces}
                  errText={maxPlacesError}
                  onBlur={setMaxPlacesTouched}
                  mask={{
                    mask: Number,
                    thousandsSeparator: '',
                    min: 0,
                    max: 999,
                    scale: 0,
                  }}
                />
                <div className={styles.helper}>
                  не включая детей младше 2 лет
                </div>
              </div>
            </div>
            <Input
              value={shortObjDescription}
              title="Краткое описание объекта*"
              placeholder="Введите описание"
              type={InputType.TEXTAREA}
              onChange={setShortObjDescription}
              errText={shortObjDescriptionError}
              onBlur={setShortObjDescriptionTouched}
              requirementsText={SHORT_DESCRIPTION_REQ}
              className={styles.description}
              maxLength={200}
            />
            <TextEditor
              title="Полное описание объекта*"
              onChange={setRentalObjDescription}
              value={rentalObjDescription}
              onBlur={setRentalObjDescriptionTouched}
              requirementsText={RENTAL_DESCRIPTION_REQ}
              errText={rentalObjDescriptionError}
              setError={setRentalObjDescriptionError}
              limit={10000}
            />
          </div>
        </div>
        <div>
          <h3 className={styles.contentTitle}>Удобства сдаваемого объекта</h3>
          <FeaturesBlock />
        </div>
        <ImageMultipleComponent
          title="Фото"
          // images={images}
          newImages={newImages}
          setNewImages={setNewImages}
          setRemovedImages={setRemovedImages}
          setError={setNewImagesError}
          errText={newImagesError}
          requirementsText={RENTAL_OBJECT_IMAGES_REQ}
          alt=""
          maxNumber={20}
          maxFileSize={2000000}
          acceptType={['jpg', 'jpeg', 'png']}
          resolutionHeight={2000}
          resolutionWidth={2000}
        />
      </div>
      <div className={styles.mainBtnsWrapper}>
        <div ref={saveButtonRef}>
          <Button
            title="Сохранить"
            type={ButtonType.PRIMARY_SMALL}
            onClick={onSaveObjectBtnClick}
            disabled={!isValid || !isFieldsChanged}
            className={styles.mainBtn}
          />
        </div>
        <Button
          title={isFieldsChanged ? 'Отмена' : 'Назад'}
          type={ButtonType.SECONDARY_SMALL}
          path={`${RoutesList.objectManagement.objectEditingChild.fullPath}/${mainId}/${TabsNames.OBJECT_EDITING}`}
          className={styles.mainBtn}
        />
      </div>
      {showFloatingSave && isValid && (
        <Button
          title="Сохранить"
          type={ButtonType.PRIMARY_SMALL}
          onClick={onSaveObjectBtnClick}
          className={styles.floatBtn}
        />
      )}
    </>
  );
};

export default RentalBasicSettings;
